// packages/mouse-management/src/store/useDeviceInfo.ts
import { create } from "zustand";
import type { MouseBoardProfileChangedDetail } from "@eweadn-qiankun/shared-utils";
import { MOUSE_BOARD_PROFILE_CHANGED_EVENT } from "@eweadn-qiankun/shared-utils";

interface BatteryInfo {
  level: number;
  charging: boolean;
  voltage?: number;
}

interface DeviceHardwareInfo {
  cid?: number;
  mid?: number;
  type?: number;
}

/**
 * 鼠标配置快照：高频字段显式列出；& Record 允许设备层扩展字段且不回落到 any。
 */
export type MouseCfgCore = {
  dpis?: unknown[];
  keysCount?: number;
  maxDpiStage?: number;
  currentDpi?: number;
  keys?: unknown[];
  shortCutKey?: unknown;
  macros?: unknown[];
  reportRate?: number;
  maxReportRateHz?: number;
  /** 富芮坤：是否允许在驱动内切换回报率 */
  reportRateSwitchable?: boolean;
  debounceTime?: number;
  sleepTime?: number;
  longDistance?: boolean;
  freqSleepLight?: unknown;
  freqScrollFlag?: unknown;
  /** 富芮坤 g2sea：唤醒方式 0 单击 / 1 移动或按键 */
  freqWakeupFlag?: unknown;
  /** 富芮坤 g2sea：灯光开启方式 0 移动不关闭 / 1 移动关闭 */
  freqMoveLightFlag?: unknown;
  dpiEffect?: {
    state?: boolean | number;
    mode?: number;
    brightness?: number;
    speed?: number;
  };
  dongleRGBBar?: unknown;
  sensor?: {
    sensorMode?: number;
    lod?: number;
    performanceState?: boolean;
    performance?: number;
    ripple?: boolean;
    angle?: boolean;
    motionSync?: boolean;
    type?: string | number;
    cfg?: { range?: unknown } & Record<string, unknown>;
    workMode?: unknown;
    fps20k?: number;
    angleTune?: number;
    virtualOffsetEnable?: boolean;
    virtualOffsetUi?: number;
  };
} & Record<string, unknown>;

export interface DeviceInfo {
  uuid?: string;
  mouseCfg: MouseCfgCore;
  battery: BatteryInfo;
  connectState: number;
  deviceOpen: boolean;
  version: Record<string, unknown>;
  info?: DeviceHardwareInfo;
  modelId?: string;
  profile?: number;
  supportChangeProfile?: boolean;
  online?: boolean;
  isWired?: boolean;
  sleeping?: boolean;
}

/** 宿主经 CustomEvent 推送的快照结构（用于校验，避免半初始化污染 store） */
export interface MouseDeviceInfoPushedPayload {
  mouseCfg: MouseCfgCore;
  /** 可选：freq C 方案等设备可能不上报电量 */
  battery?: BatteryInfo;
  uuid?: string;
  connectState?: number;
  deviceOpen?: boolean;
  version?: Record<string, unknown>;
  info?: DeviceHardwareInfo;
  modelId?: string;
  profile?: number;
  supportChangeProfile?: boolean;
  online?: boolean;
  isWired?: boolean;
  sleeping?: boolean;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

const mergeTriggerSideCfg = (
  prev: Record<string, unknown> | undefined,
  incoming: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!incoming) return prev;
  if (!prev) return incoming;
  const merged = { ...prev, ...incoming };
  if (!Object.prototype.hasOwnProperty.call(incoming, "currentRoute")) {
    merged.currentRoute = prev.currentRoute;
  }
  return merged;
};

const mergeTriggerBlock = (
  prev: Record<string, unknown> | undefined,
  incoming: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!incoming) return prev;
  if (!prev) return incoming;
  return {
    ...prev,
    ...incoming,
    left: mergeTriggerSideCfg(
      isRecord(prev.left) ? prev.left : undefined,
      isRecord(incoming.left) ? incoming.left : undefined
    ),
    right: mergeTriggerSideCfg(
      isRecord(prev.right) ? prev.right : undefined,
      isRecord(incoming.right) ? incoming.right : undefined
    ),
  };
};

/** 推送漏带 deviceOpen/connectState 时：有键表/DPI 即视为仍在会话中（切页后 store 常丢这两个字段） */
function mouseCfgLooksLive(cfg: Record<string, unknown>): boolean {
  if (Array.isArray(cfg.keys) && cfg.keys.length > 0) return true;
  if (Array.isArray(cfg.dpis) && cfg.dpis.length > 0) return true;
  if (Number(cfg.keysCount) > 0) return true;
  const scheme = cfg.freqLinkScheme;
  return scheme === "a" || scheme === "c";
}

/**
 * 校验并归一化推送 payload；不通过返回 null，不写入 store。
 * mouseCfg 必需；battery 可选（缺失时使用 level:0 / charging:false）。
 */
export function parseMouseDeviceInfoPushedPayload(
  detail: unknown
): DeviceInfo | null {
  if (!isRecord(detail)) return null;
  if (!isRecord(detail.mouseCfg)) return null;
  const prev = useDeviceInfo.getState().deviceInfo;
  const bat = detail.battery;
  const levelRaw = isRecord(bat) ? bat.level : undefined;
  const level =
    typeof levelRaw === "number" && Number.isFinite(levelRaw) ? levelRaw : 0;
  const charging = isRecord(bat) ? Boolean(bat.charging) : false;

  const version =
    detail.version != null && isRecord(detail.version)
      ? (detail.version as Record<string, unknown>)
      : {};
  const info =
    detail.info != null && isRecord(detail.info)
      ? (detail.info as DeviceHardwareInfo)
      : {};

  const prevCfg = isRecord(prev?.mouseCfg) ? prev.mouseCfg : {};
  const incomingCfg = detail.mouseCfg;
  const prevSensor = isRecord(prevCfg.sensor) ? prevCfg.sensor : {};
  const incomingSensor = isRecord(incomingCfg.sensor) ? incomingCfg.sensor : {};
  const incomingDpis = incomingCfg.dpis;
  const prevDpis = prevCfg.dpis;
  const mergedDpis =
    Array.isArray(incomingDpis) && incomingDpis.length > 0
      ? incomingDpis
      : Array.isArray(prevDpis) && prevDpis.length > 0
        ? prevDpis
        : incomingDpis;

  const mergedTrigger = mergeTriggerBlock(
    isRecord(prevCfg.trigger) ? prevCfg.trigger : undefined,
    isRecord(incomingCfg.trigger) ? incomingCfg.trigger : undefined
  );
  const mergedCal = mergeTriggerBlock(
    isRecord(prevCfg.magneticMicroswitchCal)
      ? prevCfg.magneticMicroswitchCal
      : undefined,
    isRecord(incomingCfg.magneticMicroswitchCal)
      ? incomingCfg.magneticMicroswitchCal
      : undefined
  );

  const out: DeviceInfo = {
    mouseCfg: {
      ...prevCfg,
      ...incomingCfg,
      ...(mergedDpis !== undefined ? { dpis: mergedDpis } : {}),
      ...(mergedTrigger !== undefined ? { trigger: mergedTrigger } : {}),
      ...(mergedCal !== undefined ? { magneticMicroswitchCal: mergedCal } : {}),
      sensor: { ...prevSensor, ...incomingSensor },
    } as MouseCfgCore,
    battery: { level, charging },
    connectState: (() => {
      const raw = detail.connectState;
      // mergeKeys 等推送常带着 store 初始值 0，勿当成真断开
      if (typeof raw === "number" && raw !== 0) return raw;
      if (typeof prev?.connectState === "number" && prev.connectState !== 0) {
        return prev.connectState;
      }
      if (mouseCfgLooksLive(incomingCfg)) return 0x02;
      return typeof raw === "number" ? raw : 0;
    })(),
    deviceOpen:
      typeof detail.deviceOpen === "boolean"
        ? detail.deviceOpen
        : prev?.deviceOpen === true || mouseCfgLooksLive(incomingCfg),
    version,
    info,
  };
  if (typeof detail.uuid === "string") out.uuid = detail.uuid;
  else if (typeof prev?.uuid === "string") out.uuid = prev.uuid;
  if (typeof detail.modelId === "string") out.modelId = detail.modelId;
  else if (typeof prev?.modelId === "string") out.modelId = prev.modelId;
  if (typeof detail.profile === "number") out.profile = detail.profile;
  else if (typeof prev?.profile === "number") out.profile = prev.profile;
  if (typeof detail.supportChangeProfile === "boolean") {
    out.supportChangeProfile = detail.supportChangeProfile;
  } else if (typeof prev?.supportChangeProfile === "boolean") {
    out.supportChangeProfile = prev.supportChangeProfile;
  }
  if (typeof detail.online === "boolean") out.online = detail.online;
  else if (typeof prev?.online === "boolean") out.online = prev.online;
  if (typeof detail.isWired === "boolean") out.isWired = detail.isWired;
  else if (typeof prev?.isWired === "boolean") out.isWired = prev.isWired;
  if (typeof detail.sleeping === "boolean") out.sleeping = detail.sleeping;
  else if (typeof prev?.sleeping === "boolean") out.sleeping = prev.sleeping;
  return out;
}

interface DeviceStore {
  deviceInfo: DeviceInfo;
  keys: unknown[];
  macros: unknown[];
  setDeviceInfo: (info: DeviceInfo) => void;
  setKeys: (keys: unknown[]) => void;
  setMacros: (macros: unknown[]) => void;
  resetDeviceInfo: () => void;
}

export const useDeviceInfo = create<DeviceStore>((set) => ({
  deviceInfo: {
    mouseCfg: {},
    battery: { level: 0, charging: false },
    connectState: 0x00,
    deviceOpen: false,
    version: {},
    info: {},
  },
  keys: [{}, {}, {}],
  macros: [],
  setDeviceInfo: (info) => {
    set(() => ({ deviceInfo: info }));
  },
  setKeys: (keys) => {
    set(() => ({ keys }));
  },
  setMacros: (macros) => {
    set(() => ({ macros }));
  },
  resetDeviceInfo: () => {
    set(() => ({
      deviceInfo: {
        mouseCfg: {},
        battery: { level: 0, charging: false },
        connectState: 0x00,
        deviceOpen: false,
        version: {},
        info: {},
      },
      keys: [{}, {}, {}],
      macros: [],
    }));
  },
}));

if (typeof window !== "undefined") {
  (window as unknown as { mouseStoreApi: typeof useDeviceInfo }).mouseStoreApi =
    useDeviceInfo;

  // 与键盘一致：BoardList / boardConfig 等已派发事件时，仅同步 profile 高亮
  // 完整 keys/dpi 由 syncMouseDeviceInfoToSubApp 整包推送；此处勿用旧 mouseCfg 覆盖
  window.addEventListener(MOUSE_BOARD_PROFILE_CHANGED_EVENT, (event: Event) => {
    const detail = (event as CustomEvent<MouseBoardProfileChangedDetail>)
      ?.detail;
    const profile = detail?.profile;
    if (typeof profile !== "number" || Number.isNaN(profile)) return;
    const { deviceInfo, setDeviceInfo } = useDeviceInfo.getState();
    if (deviceInfo.profile === profile) return;
    setDeviceInfo({ ...deviceInfo, profile });
  });
}
